import React, { Suspense, useCallback, useMemo } from 'react';
import styled, { StyleSheetManager } from 'styled-components';
import { PageLoading } from 'components/atoms/loading';
import { LocaleIntlProvider } from 'components/i18n/LocaleIntlProvider';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { useAccountDataQuery } from 'queries/account';
import { NotFound } from 'routes/404';
import type {
  HostAPI,
  HostComponentType,
  RenderComponentRequest,
  LoadingRenderProps,
  SheetInstanceRenderProps,
} from 'types/host-components';

const SkillBuilderApp = React.lazy(() =>
  import('agentBuilder/SkillBuilderApp').then((module) => ({
    default: module.SkillBuilderApp,
  })),
);

// Lazy: SheetInChatView is only invoked when the remote host renders a
// 'sheet_instance' request — keep the sheets graph out of the shell.
const SheetInChatView = React.lazy(() =>
  import('components/organisms/sheets/sheet-in-chat/sheet-in-chat-view').then(
    (m) => ({ default: m.SheetInChatView }),
  ),
);

const SkillBuilderWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

export const SkillBuilderRemote: React.FC = () => {
  const { data: accountData } = useAccountDataQuery();
  const { hasAccessToResource } = useWithAccess();
  const isScarletEnabled = accountData?.accountMeta?.features?.scarlet ?? false;

  const renderComponent = useCallback(
    <T extends HostComponentType>(request: RenderComponentRequest<T>) => {
      switch (request.type) {
        case 'sheet_instance': {
          const props = request.props as SheetInstanceRenderProps;

          return (
            <LocaleIntlProvider fallbackOnEmptyString>
              <Suspense fallback={null}>
                <SheetInChatView
                  projectId={props.projectId}
                  sheetId={props.sheetId}
                  tabId={props.sheetTabId}
                  readonly={props.readonly}
                  sourceType={props.sourceType}
                  sourceId={props.sourceId}
                  showInfoBar={props.showInfoBar}
                  ruleIds={props.ruleIds}
                  conversationId={props.conversationId}
                  validationType={props.validationType}
                />
              </Suspense>
            </LocaleIntlProvider>
          );
        }
        case 'loading': {
          const props = request.props as LoadingRenderProps;

          return (
            <LocaleIntlProvider fallbackOnEmptyString>
              <PageLoading size={props.size} />
            </LocaleIntlProvider>
          );
        }
        default:
          return null;
      }
    },
    [],
  );

  const hostAPI: HostAPI = useMemo(
    () => ({
      renderComponent,
      hasAccessToResource,
    }),
    [renderComponent, hasAccessToResource],
  );

  if (!isScarletEnabled) {
    return <NotFound />;
  }

  return (
    <Suspense fallback={<PageLoading />}>
      <StyleSheetManager target={document.head}>
        <SkillBuilderWrapper className="cp-pt-2 cp-pb-2 cp-pl-0 cp-pr-2">
          <SkillBuilderApp hostAPI={hostAPI} />
        </SkillBuilderWrapper>
      </StyleSheetManager>
    </Suspense>
  );
};
